import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Plus, Save, Loader2, Briefcase } from 'lucide-react';
import { JobTemplateResponse } from '../Types/JobPostTemplateType';
import { createJobTemplate, updateJobTemplate } from '../Services/JobPostTemplateService';
import { getAllOwnerShipLevel, getAllRoleLevel } from '../../Admin/Services/AdminService';
import { Option } from '../../Admin/Types/AdminType';
import { mapToOptions } from '../../Shared/Component/forms/dropdownHelpers';

interface Props {
  isOpen: boolean;
  mode: 'create' | 'edit';
  job: JobTemplateResponse | null;
  currentUserId: string;
  currentUserName: string;
  onClose: () => void;
  onSaved: (message: string) => void;
}

interface FormState {
  jobTittle: string;
  jobDescription: string;
  roleLevel: string;
  miniExperience: string;
  maxExperience: string;
  requiredOwnerShipLevel: string[];
  requiredTeamSize: string;
  requiredTechSkills: string[];
  requiredDomain: string[];
  minBudgetHandled: string;
  expectedImpactLevel: string[];
  location: string[];
  workType: string;
  noticePeriodDays: string;
  compensationOffer: string;
  jobStatus: boolean;
}

const emptyForm: FormState = {
  jobTittle: '',
  jobDescription: '',
  roleLevel: '',
  miniExperience: '',
  maxExperience: '',
  requiredOwnerShipLevel: [],
  requiredTeamSize: '',
  requiredTechSkills: [],
  requiredDomain: [],
  minBudgetHandled: '',
  expectedImpactLevel: [],
  location: [],
  workType: '',
  noticePeriodDays: '',
  compensationOffer: '',
  jobStatus: true,
};

const workTypeOptions = [
  { label: 'Hybrid', value: 'HYBRID' },
  { label: 'Onsite', value: 'ONSITE' },
  { label: 'Offshore', value: 'OFFSHORE' },
  { label: 'Work From Home', value: 'WFH' },
];

const fieldClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950 px-3.5 py-2.5 text-sm text-slate-200 placeholder-slate-600 outline-none transition [color-scheme:dark] focus:border-cyan-500/50';

const labelClass = 'mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-400';

interface TagInputProps {
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
}

const TagInput = ({ label, values, onChange, placeholder }: TagInputProps) => {
  const [input, setInput] = useState('');

  const add = () => {
    const parts = input
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);

    if (!parts.length) return;

    const next = [...values];
    parts.forEach((part) => {
      if (!next.some((v) => v.toLowerCase() === part.toLowerCase())) next.push(part);
    });

    onChange(next);
    setInput('');
  };

  return (
    <div>
      <label className={labelClass}>{label}</label>
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          placeholder={placeholder}
          className={fieldClass}
        />
        <button
          type="button"
          onClick={add}
          className="flex flex-shrink-0 items-center gap-1 rounded-xl border border-slate-800 bg-slate-900 px-3 text-xs font-black uppercase tracking-widest text-cyan-400 transition hover:border-cyan-500/40"
        >
          <Plus size={13} /> Add
        </button>
      </div>

      {values.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {values.map((value) => (
            <span
              key={value}
              className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-900 px-2.5 py-1 text-xs font-semibold text-slate-200"
            >
              {value}
              <button
                type="button"
                onClick={() => onChange(values.filter((v) => v !== value))}
                className="text-slate-500 transition hover:text-rose-400"
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const JobTemplateFormModal = ({
  isOpen,
  mode,
  job,
  currentUserId,
  currentUserName,
  onClose,
  onSaved,
}: Props) => {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [roleLevelOptions, setRoleLevelOptions] = useState<Option<string>[]>([]);
  const [ownershipOptions, setOwnershipOptions] = useState<Option<string>[]>([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    if (!isOpen) return;

    setFormError('');
    setSaving(false);

    if (mode === 'edit' && job) {
      setForm({
        jobTittle: job.jobTittle ?? '',
        jobDescription: job.jobDescription ?? '',
        roleLevel: job.roleLevel ?? '',
        miniExperience: job.miniExperience ?? '',
        maxExperience: job.maxExperience ?? '',
        requiredOwnerShipLevel: job.requiredOwnerShipLevel ?? [],
        requiredTeamSize: job.requiredTeamSize ?? '',
        requiredTechSkills: job.requiredTechSkills ?? [],
        requiredDomain: job.requiredDomain ?? [],
        minBudgetHandled: job.minBudgetHandled ?? '',
        expectedImpactLevel: job.expectedImpactLevel ?? [],
        location: job.location ?? [],
        workType: job.workType ?? '',
        noticePeriodDays: job.noticePeriodDays != null ? String(job.noticePeriodDays) : '',
        compensationOffer: job.compensationOffer != null ? String(job.compensationOffer) : '',
        jobStatus: job.jobStatus ?? true,
      });
    } else {
      setForm(emptyForm);
    }
  }, [isOpen, mode, job]);

  useEffect(() => {
    if (!isOpen || roleLevelOptions.length || ownershipOptions.length) return;

    getAllRoleLevel()
      .then((res) => {
        if (res?.data) setRoleLevelOptions(mapToOptions(res.data, 'roleLevel', 'roleLevel'));
      })
      .catch((err) => console.error('Failed to load role levels', err));

    getAllOwnerShipLevel()
      .then((res) => {
        if (res?.data) setOwnershipOptions(mapToOptions(res.data, 'ownerShipLevel', 'ownerShipLevel'));
      })
      .catch((err) => console.error('Failed to load ownership levels', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const title = useMemo(() => (mode === 'create' ? 'Create Job Template' : 'Update Job Template'), [mode]);

  const handleSubmit = async () => {
    if (!form.jobTittle.trim()) {
      setFormError('Job title is required.');
      return;
    }

    setSaving(true);
    setFormError('');

    const payload = {
      companyId: mode === 'edit' ? job?.companyId ?? currentUserId : currentUserId,
      recruiterId: mode === 'edit' ? job?.recruiterId || currentUserId : currentUserId,
      postedBy: mode === 'edit' ? job?.postedBy || currentUserName : currentUserName,
      jobTittle: form.jobTittle.trim(),
      jobDescription: form.jobDescription,
      jobSummarySlug: mode === 'edit' ? job?.jobSummarySlug ?? '' : '',
      roleLevel: form.roleLevel,
      miniExperience: form.miniExperience,
      maxExperience: form.maxExperience,
      requiredOwnerShipLevel: form.requiredOwnerShipLevel,
      requiredTeamSize: form.requiredTeamSize,
      requiredTechSkills: form.requiredTechSkills,
      requiredDomain: form.requiredDomain,
      minBudgetHandled: form.minBudgetHandled,
      expectedImpactLevel: form.expectedImpactLevel,
      location: form.location,
      workType: form.workType,
      noticePeriodDays: form.noticePeriodDays === '' ? null : Number(form.noticePeriodDays),
      compensationOffer: form.compensationOffer === '' ? null : Number(form.compensationOffer),
      jobStatus: mode === 'edit' ? form.jobStatus : true,
    };

    try {
      const response =
        mode === 'create' ? await createJobTemplate(payload) : await updateJobTemplate(job!.id, payload);

      if (response.success) {
        onSaved(mode === 'create' ? 'Job template created successfully.' : 'Job template updated successfully.');
        onClose();
      } else {
        setFormError(response.message || 'Could not save the job template.');
      }
    } catch (err) {
      console.error('Failed to save job template', err);
      setFormError('An unexpected error occurred. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/70 p-4 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            onClick={(e) => e.stopPropagation()}
            className="my-8 w-full max-w-3xl rounded-2xl border border-slate-800 bg-slate-900 shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
                  <Briefcase size={18} />
                </span>
                <div>
                  <h2 className="text-base font-black tracking-tight text-slate-100">{title}</h2>
                  <p className="text-[11px] font-semibold text-slate-500">
                    {mode === 'create' ? 'Post a new job template' : 'Edit the job template details'}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-800 hover:text-slate-200"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="max-h-[65vh] space-y-5 overflow-y-auto px-6 py-5">
              {formError && (
                <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-xs font-bold text-rose-300">
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className={labelClass}>Job Title *</label>
                  <input
                    type="text"
                    value={form.jobTittle}
                    onChange={(e) => set('jobTittle', e.target.value)}
                    placeholder="e.g. Senior QA Manager"
                    className={fieldClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Role Level</label>
                  <select
                    value={form.roleLevel}
                    onChange={(e) => set('roleLevel', e.target.value)}
                    className={fieldClass}
                  >
                    <option value="">Select role level</option>
                    {roleLevelOptions.map((option) => (
                      <option key={option.value} value={option.value} className="bg-slate-900 text-slate-100">
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Work Type</label>
                  <select
                    value={form.workType}
                    onChange={(e) => set('workType', e.target.value)}
                    className={fieldClass}
                  >
                    <option value="">Select work type</option>
                    {workTypeOptions.map((option) => (
                      <option key={option.value} value={option.value} className="bg-slate-900 text-slate-100">
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Minimum Experience</label>
                  <input
                    type="text"
                    value={form.miniExperience}
                    onChange={(e) => set('miniExperience', e.target.value)}
                    placeholder="e.g. 5 years"
                    className={fieldClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Maximum Experience</label>
                  <input
                    type="text"
                    value={form.maxExperience}
                    onChange={(e) => set('maxExperience', e.target.value)}
                    placeholder="e.g. 10 years"
                    className={fieldClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Team Size Handled</label>
                  <input
                    type="text"
                    value={form.requiredTeamSize}
                    onChange={(e) => set('requiredTeamSize', e.target.value)}
                    placeholder="e.g. 10 - 15"
                    className={fieldClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Minimum Budget Handled</label>
                  <input
                    type="text"
                    value={form.minBudgetHandled}
                    onChange={(e) => set('minBudgetHandled', e.target.value)}
                    placeholder="e.g. 500000"
                    className={fieldClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Compensation Offer</label>
                  <input
                    type="number"
                    value={form.compensationOffer}
                    onChange={(e) => set('compensationOffer', e.target.value)}
                    placeholder="e.g. 35"
                    className={fieldClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Notice Period (Days)</label>
                  <input
                    type="number"
                    value={form.noticePeriodDays}
                    onChange={(e) => set('noticePeriodDays', e.target.value)}
                    placeholder="e.g. 30"
                    className={fieldClass}
                  />
                </div>

                {mode === 'edit' && (
                  <div className="flex items-center justify-between rounded-xl border border-slate-800 bg-slate-950 px-4 py-3 md:col-span-2">
                    <div>
                      <p className="text-xs font-black uppercase tracking-widest text-slate-300">Job Status</p>
                      <p className="text-[11px] text-slate-500">Active jobs are visible to candidates.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => set('jobStatus', !form.jobStatus)}
                      className={`relative h-6 w-11 rounded-full transition ${
                        form.jobStatus ? 'bg-emerald-500' : 'bg-slate-700'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${
                          form.jobStatus ? 'left-[22px]' : 'left-0.5'
                        }`}
                      />
                    </button>
                  </div>
                )}

                <div className="md:col-span-2">
                  <label className={labelClass}>Job Description</label>
                  <textarea
                    value={form.jobDescription}
                    onChange={(e) => set('jobDescription', e.target.value)}
                    rows={4}
                    placeholder="Describe the role responsibilities and requirements..."
                    className={`${fieldClass} resize-y`}
                  />
                </div>

                <div className="md:col-span-2">
                  <TagInput
                    label="Ownership Level"
                    values={form.requiredOwnerShipLevel}
                    onChange={(values) => set('requiredOwnerShipLevel', values)}
                    placeholder="Type and press Enter (comma separated)"
                  />
                  {ownershipOptions.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {ownershipOptions
                        .filter((option) => !form.requiredOwnerShipLevel.includes(option.value))
                        .slice(0, 8)
                        .map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() =>
                              set('requiredOwnerShipLevel', [...form.requiredOwnerShipLevel, option.value])
                            }
                            className="rounded-lg border border-slate-800 bg-slate-950 px-2.5 py-1 text-[11px] font-semibold text-slate-400 transition hover:border-cyan-500/40 hover:text-cyan-300"
                          >
                            + {option.label}
                          </button>
                        ))}
                    </div>
                  )}
                </div>

                <div className="md:col-span-2">
                  <TagInput
                    label="Primary Skills"
                    values={form.requiredTechSkills}
                    onChange={(values) => set('requiredTechSkills', values)}
                    placeholder="e.g. React, Node.js"
                  />
                </div>

                <div className="md:col-span-2">
                  <TagInput
                    label="Core Domains"
                    values={form.requiredDomain}
                    onChange={(values) => set('requiredDomain', values)}
                    placeholder="e.g. Fintech, Healthcare"
                  />
                </div>

                <div className="md:col-span-2">
                  <TagInput
                    label="Expected Impact Level"
                    values={form.expectedImpactLevel}
                    onChange={(values) => set('expectedImpactLevel', values)}
                    placeholder="e.g. High"
                  />
                </div>

                <div className="md:col-span-2">
                  <TagInput
                    label="Job Locations"
                    values={form.location}
                    onChange={(values) => set('location', values)}
                    placeholder="e.g. Bengaluru, Remote"
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 border-t border-slate-800 px-6 py-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-400 transition hover:text-slate-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                className="flex items-center gap-2 rounded-xl bg-cyan-500 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-slate-950 transition hover:bg-cyan-400 disabled:opacity-50"
              >
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                {saving ? 'Saving' : mode === 'create' ? 'Create Job' : 'Save Changes'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default JobTemplateFormModal;
